import {Box, capitalize, DialogActions, DialogContent, Typography} from "@mui/material";
import {FieldArray, Form, Formik, FormikProps} from "formik";
import SubmitButton from "../../components/SubmitButton";
import {useParams} from "react-router";
import ErrorAlert from "../../components/ErrorAlert";
import {notPermitted} from "../../errors";
import MultipleGuard from "../../components/guards/MultipleGuard";
import {permissions} from "../../permissions";
import {useUnteraufgabenHandling} from "../../hooks/unteraufgaben/useUnteraufgabenHandling";
import {useAufgabe, useMandant} from "../../client/queries";
import {Aufgabe, Mandant, Unteraufgabe} from "../../api";
import {useUnteraufgabenForm} from "../../hooks/unteraufgaben/useUnteraufgabenForm";
import {unteraufgabenStyles} from "../../styles/unteraufgaben/unteraufgaben.styles";
import {closestCenter, DndContext} from "@dnd-kit/core";
import {SortableContext, verticalListSortingStrategy} from "@dnd-kit/sortable";
import SortableItem from "../../components/data/SortableList/SortableItem";
import {restrictToVerticalAxis} from "@dnd-kit/modifiers";
import UnteraufgabeItem from "../../components/unteraufgaben/UnteraufgabeItem.jsx";
import ButtonAddEmptyUnteraufgabe from "../../components/unteraufgaben/ButtonAddEmptyUnteraufgabe";
import {DEFAULT_LANGUAGE} from "../../config/language";

export default function PageEditUnteraufgaben() {
    const {aufgabeId = ""} = useParams<{
        aufgabeId: string;
    }>();
    const {
        translation,
        sensors,
        canEditUnteraufgabeField,
        useUnteraufgabenListe,
        handleDragEnd,
    } = useUnteraufgabenHandling();
    const aufgabe: Aufgabe = useAufgabe(aufgabeId);
    const mandant: Mandant = useMandant(aufgabe.mandantId);
    const {unteraufgaben, isLoading, error} = useUnteraufgabenListe(aufgabeId);
    const {formikConfig, isLoadingErwartungswerte} = useUnteraufgabenForm(aufgabe, unteraufgaben);

    if (isLoading || isLoadingErwartungswerte) return <div>Lade Unteraufgaben...</div>;


    return (
        <MultipleGuard
            permissions={[
                permissions.unteraufgaben.list,
                permissions.unteraufgaben.own_list,
                permissions.unteraufgaben.edit,
                permissions.unteraufgaben.own_edit,
                permissions.unteraufgaben.show,
                permissions.unteraufgaben.own_show,
                permissions.unteraufgaben.create,
                permissions.unteraufgaben.own_create,
            ]}
            fallback={<ErrorAlert error={notPermitted()}/>}
        >

            <Formik<{ unteraufgaben: Unteraufgabe[] }> {...formikConfig}>
                {({isSubmitting, isValidating, isValid, dirty, values, setFieldValue}: FormikProps<{
                    unteraufgaben: Unteraufgabe[]
                }>) => (
                    <Form id={'form-edit-unteraufgaben'}>
                        <Box sx={unteraufgabenStyles.pageUpdateUnteraufgabe.headerBoxWrapper}>
                            <Typography variant="h5">
                                {capitalize(translation('sub-tasks'))}
                            </Typography>
                        </Box>
                        {error ? <ErrorAlert error={error}/> : null}
                        <DialogContent id={'page-edit-unteraufgaben-dialog-content'}>
                            {
                                <FieldArray name="unteraufgaben">
                                    {({push, remove}) => (
                                        <DndContext sensors={sensors}
                                                    collisionDetection={closestCenter}
                                                    onDragEnd={(event) => handleDragEnd(
                                                        event,
                                                        values.unteraufgaben,
                                                        setFieldValue,
                                                    )}
                                                    modifiers={[restrictToVerticalAxis]}
                                        >
                                            <SortableContext
                                                items={values.unteraufgaben.map(item => item.id ?? `neu-${item.reihenfolge}`)}
                                                strategy={verticalListSortingStrategy}>
                                                {values.unteraufgaben.length === 0
                                                    ? (<Typography>
                                                        - {capitalize(translation('sub-tasks-not-found'))}
                                                    </Typography>)
                                                    : values.unteraufgaben.map((unteraufgabe, index) => (
                                                        <SortableItem id={unteraufgabe.id ?? `neu-${index}`}
                                                                      key={unteraufgabe.id ?? `neu-${index}`}
                                                                      child={<UnteraufgabeItem
                                                                          unteraufgabe={unteraufgabe}
                                                                          sprache={aufgabe.sprache ?? DEFAULT_LANGUAGE}
                                                                          mandant={mandant}
                                                                          index={index}
                                                                          remove={remove}
                                                                      />}
                                                        />
                                                    ))}
                                                <Box mt={2}>
                                                    <ButtonAddEmptyUnteraufgabe
                                                        id={'button-add-empty-unteraufgabe'}
                                                        push={push}
                                                        newIndex={values.unteraufgaben.length + 1}
                                                        aufgabeId={aufgabeId}
                                                        mandantId={mandant.id}
                                                    />
                                                </Box>
                                            </SortableContext>
                                        </DndContext>
                                    )}
                                </FieldArray>
                            }
                        </DialogContent>
                        <DialogActions id={'page-edit-unteraufgaben-dialog-actions'}>
                            <SubmitButton id={'page-edit-unteraufgaben-submit-button'}
                                          form={'form-edit-unteraufgaben'}
                                          type={'submit'}
                                          variant={'contained'}
                                          color={'secondary'}
                                          loading={isSubmitting}
                                          disabled={isValidating
                                              || !isValid
                                              || !dirty
                                              || !canEditUnteraufgabeField(aufgabe.mandantId)}
                            >
                                {isSubmitting ? capitalize(translation('updating')) : capitalize(translation('update'))}
                            </SubmitButton>
                        </DialogActions>
                    </Form>
                )
                }
            </Formik>
        </MultipleGuard>
    )
        ;
}